import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Spin, Tooltip, Typography } from 'antd'
import { filter, includes, isNil, map, some, sortBy } from 'lodash'
import PropTypes from 'prop-types'

import { areSameValue } from '../../../core/utils'

import { useLoggedInUser } from '../../../account/hooks'
import {
  checkIfQaAssignableUser,
  checkIfQcAssignableUser,
} from '../../../account/utils'
import { USER_ASSIGNMENT_ACTION_TYPE } from '../../constants'
import { useUsersQuery } from '../../hooks'
import { StyledEditabledSelectContainer } from '../styled'

import Select from './select'

const UserAssignmentSelect = ({
  assignedUser,
  onChange,
  otherAssignedUsers,
  tooltip,
  isLoading,
  userActionType,
  isDisabled,
}) => {
  const user = useLoggedInUser()
  const selectRef = useRef(null)
  const [isEditing, setIsEditing] = useState(false)

  const { data: users } = useUsersQuery()

  const usersOptions = useMemo(
    () =>
      sortBy(
        map(
          filter(users, ({ id, roles }) => {
            if (areSameValue(id, assignedUser?.id)) {
              return true
            }

            if (
              some(otherAssignedUsers, otherUserId =>
                areSameValue(id, otherUserId),
              )
            ) {
              return false
            }

            const isQaAction = includes(
              [
                USER_ASSIGNMENT_ACTION_TYPE.QA,
                USER_ASSIGNMENT_ACTION_TYPE.UNKNOWN_WORDS_QA,
              ],
              userActionType,
            )
            if (isQaAction) {
              return checkIfQaAssignableUser({ id, roles })
            }

            const isQcAction = userActionType === USER_ASSIGNMENT_ACTION_TYPE.QC
            if (isQcAction) {
              return checkIfQcAssignableUser({ id, roles })
            }

            return true
          }),
          ({ id, name }) => ({ label: name, value: id }),
        ),
        ({ value }) => (value === user?.id ? -1 : 1),
      ),
    [users, assignedUser?.id, otherAssignedUsers, user?.id, userActionType],
  )

  const handleChange = useCallback(
    value => {
      onChange(isNil(value) ? null : value)
      setIsEditing(false)
    },
    [onChange],
  )

  const handleBlur = useCallback(() => {
    setIsEditing(false)
  }, [])

  const handleClick = useCallback(
    e => {
      e.stopPropagation()

      if (isDisabled) {
        return
      }

      setIsEditing(true)
    },
    [isDisabled],
  )

  const handleSelectClick = useCallback(e => {
    e.stopPropagation()
    e.preventDefault()
  }, [])

  useEffect(() => {
    if (!isNil(selectRef.current)) {
      selectRef.current.focus()
    }
  }, [isEditing])

  if (isLoading) {
    return <Spin size="small" />
  }

  return (
    <Tooltip title={tooltip}>
      <StyledEditabledSelectContainer
        role="presentation"
        isDisabled={isDisabled}
        onClick={handleClick}
      >
        {isEditing && !isDisabled && (
          <Select
            ref={selectRef}
            showSearch
            allowClear
            onChange={handleChange}
            onBlur={handleBlur}
            onClick={handleSelectClick}
            options={usersOptions}
            value={assignedUser?.id}
            dropdownStyle={{ maxHeight: 400, minWidth: 180 }}
          />
        )}
        {!isEditing && (
          <Typography.Text>
            {assignedUser?.name || assignedUser?.email || 'unassigned'}
          </Typography.Text>
        )}
      </StyledEditabledSelectContainer>
    </Tooltip>
  )
}

UserAssignmentSelect.propTypes = {
  onChange: PropTypes.func.isRequired,
  assignedUser: PropTypes.object,
  otherAssignedUsers: PropTypes.arrayOf(PropTypes.string),
  isLoading: PropTypes.bool,
  userActionType: PropTypes.string,
  tooltip: PropTypes.string,
  isDisabled: PropTypes.bool,
}

UserAssignmentSelect.defaultProps = {
  assignedUser: undefined,
  isLoading: false,
  userActionType: undefined,
  otherAssignedUsers: [],
  tooltip: undefined,
  isDisabled: false,
}

export default UserAssignmentSelect
